"use client";

import Image from "next/image";
import { Recycle, Zap, Droplet, FileText, Target } from "lucide-react";
import { useState } from "react";

interface Contenido {
  page: number;
  titulo?: string;
  descripcion?: string;
  imagen?: string;
  objetivos?: string;
  vinetas?: string[];
}

interface Props {
  contenido: Contenido[];
}

import { useTranslations } from "next-intl";

export default function FlipCardSigat({ contenido }: Props) {
  const [modalImage, setModalImage] = useState<string | null>(null);
  const t = useTranslations("sigatPage");

  return (
    <>
      {/* CONTENIDO PRINCIPAL */}
      <div className="relative w-[80%] mx-auto text-[var(--fondoGeneral-secciones)] transition -translate-y-10">
        {/* ¿Qué es SIGAT? */}
        <section className="py-12 lg:py-16 flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
          <div className="flex-1 space-y-6">
            <h1 className="text-4xl md:text-4xl font-bold leading-tight">
              {contenido[0]?.titulo}
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed">
              {contenido[0]?.descripcion}
            </p>
            {Array.isArray(contenido[0]?.vinetas) &&
              contenido[0]?.vinetas.length > 0 && (
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  {contenido[0]?.vinetas.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              )}
          </div>
          <div className="flex-1 hidden lg:flex justify-center">
            {contenido[9]?.imagen && (
              <div className="relative w-64 h-64">
                <Image
                  src={contenido[9].imagen}
                  alt={contenido[9]?.titulo || "Imagen relacionada"}
                  fill
                  className="object-contain"
                />
              </div>
            )}
          </div>
        </section>
        {/* Política y objetivos */}
        <section className="py-12 lg:py-16">
          <div className="flex flex-col gap-8">
            {/* Título alineado a la izquierda */}
            <h2 className="text-3xl md:text-4xl font-bold text-[--fondoGeneral-secciones] text-start">
              {contenido[1]?.titulo}
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-start">
              {contenido[1]?.descripcion}
            </p>

            {/* Botones centrados */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              {contenido[1]?.imagen && (
                <button
                  onClick={() => setModalImage(contenido[1]?.imagen || "")}
                  className="w-full sm:w-72 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition 
                 bg-[color:var(--fondoGeneral-secciones)] hover:opacity-90 
                 flex items-center justify-center gap-3 text-lg sm:text-xl"
                >
                  <FileText className="w-6 h-6" /> {t("verPolitica")}
                </button>
              )}
              {contenido[1]?.objetivos && (
                <button
                  onClick={() => setModalImage(contenido[1]?.objetivos || "")}
                  className="w-full sm:w-72 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition 
                 bg-[color:var(--fondoGeneral-secciones)] hover:opacity-90 
                 flex items-center justify-center gap-3 text-lg sm:text-xl"
                >
                  <Target className="w-6 h-6" />
                  {t("verObjetivos")}
                </button>
              )}
            </div>
          </div>
        </section>
        {/* Programa ambiental */}
        <section className="py-12 lg:py-16">
          <div className="w-full">
            <h2 className="text-4xl font-bold mb-6">{contenido[2]?.titulo}</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {contenido[2]?.descripcion}
            </p>
            {Array.isArray(contenido[2]?.vinetas) && (
              <ul className="list-disc pl-6 mt-6 text-gray-600 space-y-2">
                {contenido[2]?.vinetas?.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            )}
          </div>
        </section>
        {/* Pilares y sub-secciones */}
        <section className="py-12 lg:py-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {contenido[3]?.titulo || "Pilares Ambientales"}
            </h2>
            <p className="text-gray-600 mb-12">
              {contenido[3]?.descripcion || ""}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={
                  <Droplet className="w-10 h-10 text-[var(--fondoGeneral-secciones)]" />
                }
                title={contenido[4]?.titulo || ""}
                text={contenido[4]?.descripcion || ""}
              />
              <FeatureCard
                icon={
                  <Zap className="w-10 h-10 text-[var(--fondoGeneral-secciones)]" />
                }
                title={contenido[5]?.titulo || ""}
                text={contenido[5]?.descripcion || ""}
              />
              <FeatureCard
                icon={
                  <Recycle className="w-10 h-10 text-[var(--fondoGeneral-secciones)]" />
                }
                title={contenido[6]?.titulo || ""}
                text={contenido[6]?.descripcion || ""}
              />
            </div>
          </div>
        </section>
        {/* Aspectos legales */}
        <section className="py-12 lg:py-16">
          <div className="w-full">
            <h2 className="text-4xl font-bold mb-6">{contenido[7]?.titulo}</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {contenido[7]?.descripcion}
            </p>
            {Array.isArray(contenido[7]?.vinetas) && (
              <ul className="list-disc pl-6 mt-6 text-gray-600 space-y-2">
                {contenido[7]?.vinetas?.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            )}
          </div>
        </section>
        {/* Proyectos sostenibles */}
        <section className="py-12 lg:py-16">
          <div className="w-full">
            <h2 className="text-4xl font-bold mb-6">{contenido[8]?.titulo}</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {contenido[8]?.descripcion}
            </p>
            {Array.isArray(contenido[8]?.vinetas) && (
              <ul className="list-disc pl-6 mt-6 text-gray-600 space-y-2">
                {contenido[8]?.vinetas?.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* MODAL */}
      {modalImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative rounded-xl overflow-hidden w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute  top-4 right-4  bg-black/70 hover:bg-black/90 text-white rounded-full p-2 z-20 transition"
              onClick={() => setModalImage(null)}
            >
              ✕
            </button>

            <div className="relative w-full h-[80vh]">
              <Image
                src={modalImage}
                alt="Imagen ampliada"
                fill
                className="object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* CARD GENÉRICA CON ICONO */
function FeatureCard({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2">
      <div className="flex justify-center mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-[var(--fondoGeneral-secciones)] mb-3">
        {title}
      </h3>
      <p className="text-gray-600 leading-relaxed">{text}</p>
    </div>
  );
}
